import React, {useCallback, useEffect, useState} from 'react';
import './AIChat.scss';
import Mic from "@material-ui/icons/Mic";
import Pause from "@material-ui/icons/Pause";
import VolumeUp from "@material-ui/icons/VolumeUp";
import PlayArrow from "@material-ui/icons/PlayArrow";
import VolumeOff from "@material-ui/icons/VolumeOff";
import i18next from 'i18next';
import LabeledDelayedInput from "../../Custom UI/LabeledInputs/LabeledDelayedInput/LabeledDelayedInput";
import BlueButton from "../../Custom UI/Buttons/BlueButton/BlueButton";
import Info from "../../Custom UI/Info/Info";
import LabeledDropDown from "../../Custom UI/LabeledInputs/LabeledDropDown/LabeledDropDown";
import {formatUserName} from "../../Helpers/AdminHelpers";

const AIChat = ({client, fetchAIs, patient, addAIMessage, fetchAIMessages}) => {

  // Instance Variables
  
  const [open, setOpen] = useState(false);
  
  const [voice, setVoice] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const [utterance, setUtterance] = useState(null);
  const [speakingId, setSpeakingId] = useState(undefined);
  const [voiceOptions, setVoiceOptions] = useState([]);
  
  const [asking, setAsking] = useState(false);
  const [keyword, setKeyword] = useState('default');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  
  // Effects
  
  const setVoices = () => {
    let newVoiceOptions = [];
    
    for(const voice of window.speechSynthesis.getVoices()){
      const option = {label:voice.name + ' (' + voice.lang + ')' + (voice.default ? ' — DEFAULT' : ''), value:voice.name + '-' + voice.lang, voice:voice};
      newVoiceOptions.push(option);
      
      if(option.value === 'Google US English-en-US'){
        setVoice(option);
      }
    }
    setVoiceOptions(newVoiceOptions);
  }
  
  useEffect(() => {
    setTimeout(setVoices, 1000);
    
    if(speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = setVoices;
    }
  }, []);
  
  //fetchAIMessages(aKeyword, aPatientId, aLimit, aOffset, aSearchString)
  useEffect(() => {
    setLoading(true);
    fetchAIMessages(keyword, patient && patient.id && patient.id.length > 0 ? patient.id : undefined, 100, 0, undefined).then((newResult) => {
      setLoading(false);
      setMessages([...newResult.data.data.ai_messages.ai_messages]);
    }, (newError) => {
      setLoading(false);
      console.log('Mike newError:', newError);
    })
  }, [keyword, patient]);

  // Methods
  
  const click = useCallback((aEvent) => {
    aEvent.preventDefault();
    aEvent.stopPropagation();
    setOpen(!open);
  }, [open]);
  
  //(aKeyword, aMessage, aPatientId, aFile, aClient)
  const askAI = useCallback((aMessage) => {
    setAsking(true);
    addAIMessage(keyword, aMessage, patient && patient.id && patient.id.length > 0 ? patient.id : undefined, undefined, client).then((newResult) => {
      setAsking(false);
      setMessages([newResult.data.askAiQuestionMutation.ask_ai_response, ...messages]);
      setQuestion('');
    }, (newError) => {
      setAsking(false);
      console.log('Mike newError:', newError);
    });
  }, [client, keyword, patient, messages, addAIMessage]);
  
  const getAIs = useCallback((aLimit, aOffset, aSearchString) => {
    return fetchAIs(aLimit, aOffset, aSearchString);
  }, [fetchAIs]);
  
  const getAIMessages = useCallback((aLimit, aOffset, aSearchString) => {
    return fetchAIMessages(keyword, patient && patient.id && patient.id.length > 0 ? patient.id : undefined, aLimit, aOffset, aSearchString);
  }, [keyword, patient, fetchAIMessages]);
  
  const speak = useCallback((aEvent, aText, aId) => {
    aEvent.preventDefault();
    aEvent.stopPropagation();
    
    if(utterance === null && speakingId === undefined){
      const utter = new SpeechSynthesisUtterance(aText);
      utter.voice = voice.voice;
      utter.onend = function(event) {
        setUtterance(null);
        setSpeaking(false);
        setSpeakingId(undefined);
      };
      utter.onstart = function(event) {
        setSpeakingId(aId);
        setSpeaking(true);
        
        // NOTE: This is a known bug for speechSynthesis. Just pause and resume every 14 seconds to ensure it doesn't break!
        const restart = setInterval(() => {
          if(!speechSynthesis.speaking){
            clearInterval(restart);
          }
          else{
            speechSynthesis.pause();
            speechSynthesis.resume();
          }
        }, 14000);
      };
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utter);
      setUtterance(utter);
    }
  }, [voice, speakingId, utterance]);
  
  const pause = useCallback((aEvent) => {
    aEvent.preventDefault();
    aEvent.stopPropagation();
    
    if(utterance !== null && speakingId !== undefined && speaking){
      window.speechSynthesis.pause();
      setSpeaking(false);
    }
  }, [speaking, speakingId, utterance]);
  
  const resume = useCallback((aEvent) => {
    aEvent.preventDefault();
    aEvent.stopPropagation();
    
    if(utterance !== null && speakingId !== undefined && !speaking){
      window.speechSynthesis.resume();
      setSpeaking(true);
    }
  }, [speaking, speakingId, utterance]);
  
  // Effects

  return (
      open ?
        <div className="ai-chat-container">
          <div className="ai-chat-title">
            {i18next.t((patient ? (formatUserName(patient).length > 0 ? 'AI_CHAT_PATIENT_DESCRIPTION' : 'AI_CHAT_DEFAULT_PATIENT_DESCRIPTION') : 'AI_CHAT_DESCRIPTION') , {aName:formatUserName(patient)})}
          </div>
          
          <div className="ai-chat-close-button"
               onClick={loading ? undefined : click}>
            X
          </div>
          
          <div className="ai-chat-dropdown-container">
            <LabeledDropDown className="ai-chat-dropdown"
                             value={keyword}
                             disabled={loading}
                             handleSave={(aAI) => setKeyword(aAI.keyword)}
                             placeholder={i18next.t('SELECT_AI')}
                             fetchObjects={getAIs}
                             saveWithOptionsKey={true}
                             labelPropertiesArray={['name']}
                             valuePropertiesArray={['keyword']}
                             responseTotalKeysArray={['data', 'data', 'ai_models', 'total']}
                             responseObjectKeysArray={['data', 'data', 'ai_models', 'ai_models']}
            />
            
            <LabeledDropDown className="ai-chat-dropdown"
                             value={voice}
                             options={voiceOptions}
                             disabled={loading}
                             handleSave={(aOption) => setVoice(aOption)}
            />
          </div>
          
          <div className="ai-chat-messages-container">
            {messages.map((message, index) =>
              <div className="ai-chat-message-container"
                   key={message.question_message.id}>
                <div className="ai-chat-question-container">
                  <div className="ai-chat-question">
                    {message.question_message.message_text}
                  </div>
                </div>
                
                <div className="ai-chat-ai-message-container">
                  <div className="ai-chat-ai-message">
                    {message.response_message.message_text}
                  </div>
                  
                  {speakingId === undefined ?
                    <VolumeUp style={{fontSize:'20px', cursor:'pointer'}}
                              onClick={loading || asking ? undefined : (aEvent) => speak(aEvent, message.response_message.message_text, message.question_message.id)}
                    />
                    :
                    speakingId === message.question_message.id ?
                      speaking ?
                        <Pause style={{fontSize:'20px', cursor:'pointer'}}
                               onClick={loading || asking ? undefined : pause}
                        />
                        :
                        <PlayArrow style={{fontSize:'20px', cursor:'pointer'}}
                                   onClick={loading || asking ? undefined : resume}
                        />
                      :
                      <VolumeOff style={{fontSize:'20px', cursor:'not-allowed'}}/>
                  }
                </div>
              </div>
            )}
          </div>
          
          <div className="ai-chat-input-container">
            <LabeledDelayedInput className="ai-chat-input"
                                 value={question}
                                 disabled={loading || asking}
                                 minLength={0}
                                 onKeyDown={(aEvent) => {
                                   if(aEvent && aEvent.key === 'Enter'){
                                     askAI(aEvent.target.value);
                                   }
                                 }}
                                 handleSave={(aEvent) => setQuestion(aEvent.target.value)}
                                 placeholder={i18next.t('ASK_A_QUESTION')}
            />
            
            <BlueButton className="ai-chat-ask-button"
                        name={i18next.t('ASK')}
                        onClick={() => askAI(question)}
                        loading={asking}
                        disabled={loading}
                        
            />
            
            <Mic style={{fontSize:'20px', cursor:'pointer'}}
                 onClick={loading || asking ? undefined : undefined}
            />
          </div>
        </div>
        :
        <div className="ai-chat-container-small"
             onClick={click}>
          <img className="ai-chat-logo"
               src={'/imgs/app/hsc-logo.png'}
               alt={i18next.t('AI_CHAT')}
          />
        </div>
  );
};

export default AIChat;
